import { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { Upload, FileText, Link2, ShieldCheck, CheckCircle2, Clock, AlertTriangle, XCircle, CalendarClock, Banknote, Hourglass, Pencil, ImageIcon, TrendingUp, ClipboardPaste, StickyNote } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SECTION_CFG = {
  success: { label: 'Repaid', class: 'bg-emerald-100 text-emerald-700', icon: CheckCircle2 },
  future: { label: 'Future', class: 'bg-blue-100 text-blue-700', icon: CalendarClock },
  tracking: { label: 'Tracking', class: 'bg-amber-100 text-amber-700', icon: Clock },
  failed: { label: 'Overdue', class: 'bg-red-100 text-red-700', icon: AlertTriangle },
  reversed: { label: 'Overdue (Reversed)', class: 'bg-red-100 text-red-700', icon: AlertTriangle },
  cancelled: { label: 'Cancelled', class: 'bg-gray-200 text-gray-600', icon: XCircle },
};

const fmtR = (v) => `R${(v || 0).toLocaleString('en-ZA', { minimumFractionDigits: 2 })}`;

export default function MandateCollections({ getAuthHeader, onLoansUpdated, showFinancials = true }) {
  const [mandates, setMandates] = useState([]);
  const [reports, setReports] = useState([]);
  const [stats, setStats] = useState(null);
  const [monthly, setMonthly] = useState([]);
  const [sectionFilter, setSectionFilter] = useState('all');
  const [uploading, setUploading] = useState(false);
  const [linkDialog, setLinkDialog] = useState({ open: false, mandate: null });
  const [linkRef, setLinkRef] = useState('');
  const [linking, setLinking] = useState(false);
  const [editDialog, setEditDialog] = useState({ open: false, mandate: null });
  const [editForm, setEditForm] = useState({ action_date: '', instalments_total: '', total_amount: '', section: 'future', manual_note: '' });
  const [pastedImage, setPastedImage] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const fileRef = useRef(null);
  const trackerRef = useRef(null);
  const mandateRepRef = useRef(null);
  const [uploadingTracker, setUploadingTracker] = useState(false);
  const [uploadingMandateRep, setUploadingMandateRep] = useState(false);
  const [detailDialog, setDetailDialog] = useState({ open: false, loading: false, data: null });
  const [offenders, setOffenders] = useState({ open: false, times: 0, loading: false, list: [] });

  const openOffenders = async (times) => {
    setOffenders({ open: true, times, loading: true, list: [] });
    try {
      const res = await axios.get(`${API_URL}/admin/collections/overdue-offenders?times=${times}`, getAuthHeader());
      setOffenders({ open: true, times, loading: false, list: res.data.offenders });
    } catch {
      toast.error('Could not load offenders');
      setOffenders(o => ({ ...o, open: false }));
    }
  };

  const toggleHandover = async (mandateId, handed) => {
    try {
      await axios.post(`${API_URL}/admin/collections/mandates/${mandateId}/handover`, { handed_over: handed }, getAuthHeader());
      toast.success(handed ? 'Marked for handover' : 'Handover flag removed');
      setOffenders(o => ({ ...o, list: o.list.map(x => x.mandate_id === mandateId ? { ...x, handed_over: handed } : x) }));
      fetchMandates();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const openDetail = async (m) => {
    setDetailDialog({ open: true, loading: true, data: null });
    try {
      const res = await axios.get(`${API_URL}/admin/collections/mandates/${m.id}/detail`, getAuthHeader());
      setDetailDialog({ open: true, loading: false, data: res.data });
    } catch (e) {
      toast.error('Could not load mandate detail');
      setDetailDialog({ open: false, loading: false, data: null });
    }
  };

  const uploadMandateReport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingMandateRep(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API_URL}/admin/collections/upload-mandate-report`, fd, {
        headers: { ...getAuthHeader().headers, 'Content-Type': 'multipart/form-data' },
      });
      const d = res.data;
      toast.success(`Mandate report processed: ${d.total} mandate(s) — ${d.updated} enriched, ${d.created} new, ${d.matched} newly matched to loans, ${d.rejected} rejected/inactive`);
      fetchMandates();
      onLoansUpdated && onLoansUpdated();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Mandate report upload failed');
    }
    setUploadingMandateRep(false);
    if (mandateRepRef.current) mandateRepRef.current.value = '';
  };
  const shotFileRef = useRef(null);

  const fetchMandates = useCallback(async () => {
    try {
      const [res, statsRes, monthlyRes] = await Promise.all([
        axios.get(`${API_URL}/admin/collections/mandates?section=${sectionFilter}`, getAuthHeader()),
        axios.get(`${API_URL}/admin/collections/stats`, getAuthHeader()),
        axios.get(`${API_URL}/admin/collections/monthly`, getAuthHeader()),
      ]);
      setMandates(res.data.mandates || []);
      setReports(res.data.recent_reports || []);
      setStats(statsRes.data);
      setMonthly(monthlyRes.data || []);
    } catch (e) { toast.error('Failed to load mandates'); }
  }, [getAuthHeader, sectionFilter]);

  useEffect(() => { fetchMandates(); }, [fetchMandates]);

  const uploadReport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API_URL}/admin/collections/upload-report`, fd, {
        headers: { ...getAuthHeader().headers, 'Content-Type': 'multipart/form-data' },
      });
      const d = res.data;
      toast.success(`Report processed: ${d.total} mandate(s) — ${d.matched} matched (${d.repaid} repaid, ${d.future} future, ${d.tracking} tracking, ${d.overdue} overdue), ${d.unmatched} unmatched`);
      fetchMandates();
      onLoansUpdated && onLoansUpdated();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = '';
  };

  const uploadTracker = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingTracker(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API_URL}/admin/collections/upload-tracker`, fd, {
        headers: { ...getAuthHeader().headers, 'Content-Type': 'multipart/form-data' },
      });
      const d = res.data;
      toast.success(`Tracker processed: ${d.mandates_in_file} mandate(s) — ${d.updated} set to tracking, ${d.skipped_repaid} already repaid (kept), ${d.unmatched} unmatched`);
      if (d.unmatched_list?.length) toast.info(`Unmatched mandate IDs: ${d.unmatched_list.map(u => u.mandate_id).join(', ')}`);
      fetchMandates();
      onLoansUpdated && onLoansUpdated();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Tracker upload failed');
    }
    setUploadingTracker(false);
    if (trackerRef.current) trackerRef.current.value = '';
  };

  const linkMandate = async () => {
    setLinking(true);
    try {
      const res = await axios.post(`${API_URL}/admin/collections/mandates/${linkDialog.mandate.id}/link`,
        { reference: linkRef }, getAuthHeader());
      toast.success(res.data.message);
      setLinkDialog({ open: false, mandate: null });
      setLinkRef('');
      fetchMandates();
      onLoansUpdated && onLoansUpdated();
    } catch (e) { toast.error(e.response?.data?.detail || 'Link failed'); }
    setLinking(false);
  };

  const openEdit = (m) => {
    setEditForm({
      action_date: m.action_date || '',
      instalments_total: m.instalments_total || '',
      total_amount: m.total_amount ?? '',
      section: m.section || 'future',
      manual_note: m.manual_note || '',
    });
    setPastedImage(null);
    setEditDialog({ open: true, mandate: m });
  };

  const handlePaste = (e) => {
    const items = e.clipboardData?.items || [];
    for (const item of items) {
      if (item.type.startsWith('image/')) {
        const f = item.getAsFile();
        if (f) {
          setPastedImage({ file: f, url: URL.createObjectURL(f) });
          toast.success('Screenshot captured — it will be attached on Save');
        }
        return;
      }
    }
  };

  const pickScreenshotFile = (e) => {
    const f = e.target.files?.[0];
    if (f && f.type.startsWith('image/')) setPastedImage({ file: f, url: URL.createObjectURL(f) });
    if (shotFileRef.current) shotFileRef.current.value = '';
  };

  const saveEdit = async () => {
    setSavingEdit(true);
    try {
      const body = {
        action_date: editForm.action_date,
        section: editForm.section,
        instalments_total: editForm.instalments_total === '' ? null : parseInt(editForm.instalments_total, 10),
        total_amount: editForm.total_amount === '' ? null : parseFloat(editForm.total_amount),
        manual_note: editForm.manual_note,
      };
      await axios.put(`${API_URL}/admin/collections/mandates/${editDialog.mandate.id}`, body, getAuthHeader());
      if (pastedImage) {
        const fd = new FormData();
        fd.append('file', pastedImage.file, 'mandate-screenshot.png');
        await axios.post(`${API_URL}/admin/collections/mandates/${editDialog.mandate.id}/screenshot`, fd, {
          headers: { ...getAuthHeader().headers, 'Content-Type': 'multipart/form-data' },
        });
      }
      toast.success(pastedImage ? 'Mandate updated & screenshot attached to the loan' : 'Mandate updated');
      setEditDialog({ open: false, mandate: null });
      setPastedImage(null);
      fetchMandates();
      onLoansUpdated && onLoansUpdated();
    } catch (e) { toast.error(e.response?.data?.detail || 'Update failed'); }
    setSavingEdit(false);
  };

  const viewScreenshot = async (fileId) => {
    try {
      const res = await axios.get(`${API_URL}/client/documents/${fileId}`, { ...getAuthHeader(), responseType: 'blob' });
      window.open(URL.createObjectURL(res.data), '_blank');
    } catch (e) { toast.error('Could not open screenshot'); }
  };

  const exportCsv = async () => {
    try {
      const res = await axios.get(`${API_URL}/admin/collections/export-csv`, { ...getAuthHeader(), responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `collections_mandates_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('Collections CSV downloaded');
    } catch (e) { toast.error('Export failed'); }
  };

  const counts = mandates.reduce((acc, m) => {
    const k = SECTION_CFG[m.section] ? m.section : 'other';
    acc[k] = (acc[k] || 0) + 1;
    if (!m.matched_app_id) acc.unmatched = (acc.unmatched || 0) + 1;
    return acc;
  }, {});

  return (
    <Card className="border-blue-200">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <CardTitle className="flex items-center gap-2 text-lg"><ShieldCheck className="w-5 h-5 text-blue-600" />DebiCheck Mandates (NuPay)</CardTitle>
            <CardDescription>Upload the NuPay Transaction Report — loans are matched by mandate reference, loan reference or ID number, then marked repaid / tracking / overdue automatically</CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={exportCsv} data-testid="export-collections-csv-btn">
              <FileText className="w-4 h-4 mr-1.5" />Export CSV
            </Button>
            <input type="file" accept=".pdf,.xlsx,.xlsm" ref={fileRef} onChange={uploadReport} className="hidden" data-testid="nupay-file-input" />
            <input type="file" accept=".xlsx,.xlsm" ref={trackerRef} onChange={uploadTracker} className="hidden" data-testid="tracker-file-input" />
            <input type="file" accept=".pdf" ref={mandateRepRef} onChange={uploadMandateReport} className="hidden" data-testid="mandate-report-file-input" />
            <Button variant="outline" onClick={() => mandateRepRef.current?.click()} disabled={uploadingMandateRep} className="border-indigo-300 text-indigo-700 hover:bg-indigo-50" data-testid="upload-mandate-report-btn">
              <ShieldCheck className="w-4 h-4 mr-2" />{uploadingMandateRep ? 'Processing…' : 'Upload Mandate Report'}
            </Button>
            <Button variant="outline" onClick={() => trackerRef.current?.click()} disabled={uploadingTracker} className="border-amber-300 text-amber-700 hover:bg-amber-50" data-testid="upload-tracker-btn">
              <Clock className="w-4 h-4 mr-2" />{uploadingTracker ? 'Processing…' : 'Upload Tracker (xlsx)'}
            </Button>
            <Button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="upload-nupay-btn">
              <Upload className="w-4 h-4 mr-2" />{uploading ? 'Processing…' : 'Upload NuPay Report'}
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {stats?.tracking_expired?.count > 0 && (
          <button className="w-full flex items-center gap-2 rounded-lg border border-red-300 bg-red-50 p-3 text-left hover:bg-red-100 transition-colors" data-testid="tracking-expiry-alert"
            onClick={() => setSectionFilter('tracking')} title="Click to view lapsed tracking mandates">
            <AlertTriangle className="w-5 h-5 text-red-600 shrink-0" />
            <div>
              <p className="text-sm font-semibold text-red-800">{stats.tracking_expired.count} mandate(s) lapsed tracking without repayment — {fmtR(stats.tracking_expired.amount)}</p>
              <p className="text-[11px] text-red-600">The tracking window has ended and no money came in. Follow up with these clients today.</p>
            </div>
          </button>
        )}
        {stats && (
          <div className="grid grid-cols-2 lg:grid-cols-6 gap-3" data-testid="collections-stats">
            <div className="rounded-lg border border-emerald-100 bg-emerald-50/60 p-3" data-testid="stat-collected-month">
              <p className="text-[11px] font-semibold text-emerald-700 flex items-center gap-1"><Banknote className="w-3.5 h-3.5" />COLLECTED THIS MONTH</p>
              <p className="text-xl font-bold text-emerald-700 tabular-nums mt-1">{fmtR(stats.collected_month.amount)}</p>
              <p className="text-[11px] text-emerald-600">{stats.collected_month.count} payment(s) · {fmtR(stats.collected_total.amount)} all-time</p>
            </div>
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3" data-testid="stat-outstanding">
              <p className="text-[11px] font-semibold text-slate-600 flex items-center gap-1"><Hourglass className="w-3.5 h-3.5" />STILL DUE</p>
              <p className="text-xl font-bold text-slate-800 tabular-nums mt-1">{fmtR(stats.outstanding.amount)}</p>
              <p className="text-[11px] text-slate-500">{stats.outstanding.count} mandate(s) not yet collected</p>
            </div>
            <div className="rounded-lg border border-blue-100 bg-blue-50/60 p-3" data-testid="stat-upcoming">
              <p className="text-[11px] font-semibold text-blue-700 flex items-center gap-1"><CalendarClock className="w-3.5 h-3.5" />UPCOMING COLLECTIONS</p>
              <p className="text-xl font-bold text-blue-700 tabular-nums mt-1">{fmtR(stats.upcoming.amount)}</p>
              <p className="text-[11px] text-blue-600">{stats.upcoming.count} scheduled{stats.upcoming.next_date ? ` · next ${stats.upcoming.next_date}` : ''}</p>
              {showFinancials && stats.upcoming.expected_profit != null && stats.upcoming.count > 0 && (
                <p className="text-[11px] font-semibold text-teal-700 mt-0.5" data-testid="stat-upcoming-profit">
                  expected profit {fmtR(stats.upcoming.expected_profit)}
                </p>
              )}
            </div>
            {stats.tracking && (
              <button className="rounded-lg border border-amber-200 bg-amber-50/60 p-3 text-left cursor-pointer hover:border-amber-400 transition-colors" data-testid="stat-tracking"
                onClick={() => setSectionFilter(sectionFilter === 'tracking' ? 'all' : 'tracking')} title="Click to filter tracking mandates">
                <p className="text-[11px] font-semibold text-amber-700 flex items-center gap-1"><Clock className="w-3.5 h-3.5" />TRACKING</p>
                <p className="text-xl font-bold text-amber-700 tabular-nums mt-1">{fmtR(stats.tracking.amount)}</p>
                <p className="text-[11px] text-amber-600">{stats.tracking.count} in tracking / past date</p>
                {stats.tracking_expired?.count > 0 && (
                  <p className="text-[11px] font-semibold text-red-600" data-testid="stat-tracking-expired">{stats.tracking_expired.count} lapsed — follow up</p>
                )}
              </button>
            )}
            <div className="rounded-lg border border-red-100 bg-red-50/60 p-3" data-testid="stat-overdue">
              <p className="text-[11px] font-semibold text-red-700 flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" />OVERDUE</p>
              <p className="text-xl font-bold text-red-700 tabular-nums mt-1">{fmtR(stats.overdue.amount)}</p>
              <p className="text-[11px] text-red-600">{stats.overdue.count} failed debit order(s)</p>
              {stats.overdue.breakdown && stats.overdue.mandates > 0 && (
                <div className="flex flex-wrap gap-1 mt-1" data-testid="overdue-breakdown">
                  {Object.entries(stats.overdue.breakdown).filter(([, v]) => v.mandates > 0).map(([k, v]) => (
                    <button key={k} onClick={() => openOffenders(parseInt(k.slice(1), 10))}
                      title={`${v.mandates} mandate(s) with ${k.slice(1)}${k === 'x5' ? '+' : ''} missed payment(s) — ${fmtR(v.amount)} · click for the client list`}
                      className="inline-flex items-center rounded bg-red-100 text-red-700 px-1.5 py-0.5 text-[10px] font-bold hover:bg-red-200 cursor-pointer" data-testid={`overdue-${k}`}>
                      {k}{k === 'x5' ? '+' : ''}: {v.mandates}
                    </button>
                  ))}
                </div>
              )}
            </div>
            {showFinancials && stats.profit && (
              <div className="rounded-lg border border-teal-100 bg-teal-50/60 p-3" data-testid="stat-gross-profit">
                <p className="text-[11px] font-semibold text-teal-700 flex items-center gap-1"><TrendingUp className="w-3.5 h-3.5" />GROSS PROFIT</p>
                <p className="text-xl font-bold text-teal-700 tabular-nums mt-1">{fmtR(stats.profit.gross_profit)}</p>
                <p className="text-[11px] text-teal-600">{stats.profit.margin_pct}% margin · {fmtR(stats.profit.revenue)} collected − {fmtR(stats.profit.capital)} capital</p>
              </div>
            )}
          </div>
        )}
        {monthly.length > 0 && (
          <div className="rounded-lg border border-slate-100 p-3" data-testid="monthly-collections-chart">
            <p className="text-xs font-semibold text-slate-600 mb-2">Monthly: Collected vs Due</p>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={monthly} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `R${v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v}`} />
                <Tooltip formatter={(v, name) => [fmtR(v), name === 'collected' ? 'Collected' : 'Due']} />
                <Legend formatter={(v) => v === 'collected' ? 'Collected' : 'Due'} wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="collected" fill="#059669" radius={[3, 3, 0, 0]} />
                <Bar dataKey="due" fill="#f59e0b" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
        <div className="flex flex-wrap gap-2" data-testid="mandate-summary">
          {Object.entries(SECTION_CFG).map(([k, cfg]) => (
            counts[k] ? <Badge key={k} variant="outline" className={`${cfg.class} border-0`}>{cfg.label}: {counts[k]}</Badge> : null
          ))}
          {counts.unmatched ? <Badge variant="outline" className="bg-orange-100 text-orange-700 border-0">Unmatched: {counts.unmatched}</Badge> : null}
        </div>

        <div className="flex items-center gap-2">
          <Select value={sectionFilter} onValueChange={setSectionFilter}>
            <SelectTrigger className="w-[180px] h-9 text-sm" data-testid="mandate-section-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Sections</SelectItem>
              <SelectItem value="success">Paid (Repaid)</SelectItem>
              <SelectItem value="future">Future</SelectItem>
              <SelectItem value="tracking">Tracking</SelectItem>
              <SelectItem value="failed">Overdue (Failed)</SelectItem>
              <SelectItem value="reversed">Reversed</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
            </SelectContent>
          </Select>
          {reports[0] && (
            <p className="text-xs text-slate-400 flex items-center gap-1"><FileText className="w-3 h-3" />Last report: {reports[0].filename} — {new Date(reports[0].uploaded_at).toLocaleString()} by {reports[0].uploaded_by}</p>
          )}
        </div>

        {mandates.length === 0 ? (
          <p className="text-center text-slate-500 py-6 text-sm">No mandates yet — upload a NuPay Transaction Report to get started</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="mandates-table">
              <thead>
                <tr className="border-b text-left text-xs text-slate-500">
                  <th className="py-2 px-3">Mandate</th>
                  <th className="py-2 px-3">Matched Loan</th>
                  <th className="py-2 px-3">Bank</th>
                  <th className="py-2 px-3">Repayment Date</th>
                  <th className="py-2 px-3">Amount Due</th>
                  <th className="py-2 px-3">Status</th>
                  <th className="py-2 px-3"></th>
                </tr>
              </thead>
              <tbody>
                {mandates.map((m) => {
                  const cfg = SECTION_CFG[m.section] || { label: m.section, class: 'bg-slate-100 text-slate-600' };
                  return (
                    <tr key={m.id} className="border-b border-slate-50 hover:bg-slate-50/60 cursor-pointer" data-testid={`mandate-row-${m.mandate_id}`} onClick={() => openDetail(m)} title="Click for full loan & collection detail">
                      <td className="py-2.5 px-3">
                        <p className="font-mono text-sm font-semibold">{m.mandate_id}</p>
                        <p className="font-mono text-[10px] text-slate-400 break-all">{m.mandate_reference}</p>
                        {(m.client_ref || m.contract_reference) && <p className="text-[10px] text-slate-500">{m.client_ref || m.contract_reference}</p>}
                      </td>
                      <td className="py-2.5 px-3">
                        {m.matched_app_id ? (
                          <>
                            <p className="font-mono text-sm">{m.matched_reference}</p>
                            <p className="text-xs text-slate-500">{m.matched_customer}</p>
                            {m.principal != null && <p className="text-[10px] font-semibold text-emerald-700">principal {fmtR(m.principal)}</p>}
                            <p className="text-[10px] text-slate-400">via {m.match_method?.replace('_', ' ')}</p>
                          </>
                        ) : m.off_book ? (
                          <>
                            <Badge variant="outline" className="bg-purple-100 text-purple-700 border-0 text-[10px]" data-testid={`offbook-badge-${m.mandate_id}`}>Off-book loan</Badge>
                            <p className="font-mono text-xs text-purple-700 mt-0.5">{m.manual_reference}</p>
                            {m.off_book_by && <p className="text-[10px] text-slate-400">by {m.off_book_by}</p>}
                          </>
                        ) : (
                          <Badge variant="outline" className="bg-orange-100 text-orange-700 border-0 text-[10px]">Unmatched</Badge>
                        )}
                      </td>
                      <td className="py-2.5 px-3 text-sm">{m.debtor_bank}</td>
                      <td className="py-2.5 px-3 text-sm tabular-nums">
                        {m.action_date || m.cycle_date || '-'}
                        <p className="text-[10px] text-slate-400">instalment {m.instalment_number || 1}{m.instalments_total ? ` of ${m.instalments_total}` : ''}</p>
                      </td>
                      <td className="py-2.5 px-3 text-sm font-semibold tabular-nums">
                        {m.section === 'success' ? <span className="text-emerald-600">{fmtR(m.total_amount)} collected</span> : fmtR(m.total_amount)}
                        <p className="text-[10px] text-slate-400 font-normal">loan {fmtR(m.original_amount)} + fees {fmtR(m.pif_value)}</p>
                      </td>
                      <td className="py-2.5 px-3">
                        <Badge variant="outline" className={`${cfg.class} border-0 text-[11px]`}>{cfg.label}</Badge>
                        {m.handed_over && <Badge variant="outline" className="bg-purple-600 text-white border-0 text-[9px] ml-1" data-testid={`handover-badge-${m.mandate_id}`}>HANDED OVER</Badge>}
                        {(m.section === 'failed' || m.section === 'reversed') && (
                          <p className="text-[10px] text-red-600 font-medium mt-0.5" data-testid={`missed-reason-${m.mandate_id}`}>
                            Missed payment{m.response_reason ? ` — ${m.response_reason}` : ''} · edit to move date
                          </p>
                        )}
                        {m.section === 'tracking' && m.tracking_expired && (
                          <Badge variant="outline" className="bg-red-100 text-red-700 border-red-300 text-[10px] mt-0.5 animate-pulse" data-testid={`tracking-expired-${m.mandate_id}`}>
                            Tracking lapsed {m.tracking_until_effective ? `· ${m.tracking_until_effective}` : ''} — follow up
                          </Badge>
                        )}
                        {m.section === 'tracking' && !m.tracking_expired && m.tracking_days != null && (
                          <p className="text-[10px] text-amber-700 mt-0.5" data-testid={`tracking-period-${m.mandate_id}`}>
                            {m.tracking_days}d tracking{m.tracking_until ? ` · until ${m.tracking_until}` : ''}
                          </p>
                        )}
                        {m.manual_note && (
                          <p className="text-[10px] text-slate-500 italic mt-0.5 max-w-[180px] truncate flex items-center gap-1" title={`${m.manual_note}${m.manual_note_by ? ` — ${m.manual_note_by}` : ''}`} data-testid={`mandate-note-${m.mandate_id}`}>
                            <StickyNote className="w-3 h-3 shrink-0 text-amber-500" />{m.manual_note}
                          </p>
                        )}
                      </td>
                      <td className="py-2.5 px-3" onClick={e => e.stopPropagation()}>
                        <div className="flex gap-1">
                          <Button size="sm" variant="ghost" onClick={() => openEdit(m)} title="Edit mandate" data-testid={`edit-mandate-${m.mandate_id}`}>
                            <Pencil className="w-3.5 h-3.5" />
                          </Button>
                          {m.screenshot_file_id && (
                            <Button size="sm" variant="ghost" className="text-blue-600" onClick={() => viewScreenshot(m.screenshot_file_id)} title="View attached screenshot" data-testid={`view-screenshot-${m.mandate_id}`}>
                              <ImageIcon className="w-3.5 h-3.5" />
                            </Button>
                          )}
                          {!m.matched_app_id && (
                            <Button size="sm" variant="outline" onClick={() => { setLinkDialog({ open: true, mandate: m }); setLinkRef(''); }} data-testid={`link-mandate-${m.mandate_id}`}>
                              <Link2 className="w-3.5 h-3.5 mr-1" />Link
                            </Button>
                          )}
                          {!m.matched_app_id && !m.off_book && (
                            <Button size="sm" variant="outline" className="text-purple-700 border-purple-200 hover:bg-purple-50" data-testid={`offbook-mandate-${m.mandate_id}`}
                              onClick={async () => {
                                const ref = window.prompt('Off-book loan reference (e.g. OB-2026-001 or client name):');
                                if (!ref || !ref.trim()) return;
                                try {
                                  await axios.post(`${API_URL}/admin/collections/mandates/${m.id}/offbook`, { manual_reference: ref.trim() }, getAuthHeader());
                                  toast.success('Tagged as off-book loan');
                                  fetchMandates();
                                } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
                              }}>
                              Off-book
                            </Button>
                          )}
                          {m.off_book && (
                            <Button size="sm" variant="ghost" className="text-red-400 h-8" title="Remove off-book tag" data-testid={`offbook-remove-${m.mandate_id}`}
                              onClick={async () => {
                                try {
                                  await axios.post(`${API_URL}/admin/collections/mandates/${m.id}/offbook`, { off_book: false }, getAuthHeader());
                                  toast.success('Off-book tag removed');
                                  fetchMandates();
                                } catch { toast.error('Failed'); }
                              }}>
                              <XCircle className="w-3.5 h-3.5" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>

      <Dialog open={offenders.open} onOpenChange={(o) => setOffenders(s => ({ ...s, open: o }))}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="offenders-dialog">
          <DialogHeader>
            <DialogTitle>Repeat Offenders — missed x{offenders.times}{offenders.times === 5 ? '+' : ''}</DialogTitle>
          </DialogHeader>
          {offenders.loading && <p className="text-sm text-slate-500 py-4 text-center">Loading…</p>}
          {!offenders.loading && offenders.list.length === 0 && <p className="text-sm text-slate-500 py-4 text-center">No mandates in this bucket.</p>}
          <div className="space-y-2">
            {offenders.list.map(o => (
              <div key={o.mandate_id} className={`rounded-lg border p-3 flex flex-wrap items-center gap-3 ${o.handed_over ? 'border-purple-300 bg-purple-50/60' : 'border-red-100 bg-red-50/40'}`} data-testid={`offender-${o.mandate_id}`}>
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-sm text-slate-800">{o.customer}
                    {o.handed_over && <Badge variant="outline" className="ml-2 bg-purple-600 text-white border-0 text-[9px]">HANDED OVER{o.handed_over_by ? ` · ${o.handed_over_by}` : ''}</Badge>}
                  </p>
                  <p className="text-[11px] text-slate-500">Mandate {o.mandate_id}{o.reference ? ` · Ref ${o.reference}` : ''} · missed x{o.missed} · last {o.last_missed || '—'}</p>
                  {o.reasons?.length > 0 && <p className="text-[11px] text-red-600">{o.reasons.join(', ')}</p>}
                </div>
                <div className="text-right">
                  <p className="font-bold tabular-nums text-red-700">{fmtR(o.amount)}</p>
                  {o.phone ? (
                    <a href={`tel:${o.phone}`} className="text-[12px] font-semibold text-blue-700 hover:underline" data-testid={`offender-phone-${o.mandate_id}`}>{o.phone}</a>
                  ) : <p className="text-[11px] text-slate-400">no number</p>}
                </div>
                <Button size="sm" variant={o.handed_over ? 'outline' : 'default'}
                  className={o.handed_over ? 'h-7 text-xs border-purple-300 text-purple-700' : 'h-7 text-xs bg-purple-700 hover:bg-purple-800'}
                  onClick={() => toggleHandover(o.mandate_id, !o.handed_over)} data-testid={`handover-btn-${o.mandate_id}`}>
                  {o.handed_over ? 'Undo Handover' : 'Mark for Handover'}
                </Button>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={detailDialog.open} onOpenChange={(o) => setDetailDialog(d => ({ ...d, open: o }))}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="mandate-detail-dialog">
          <DialogHeader><DialogTitle>Loan & Collection Detail — Mandate {detailDialog.data?.mandate?.mandate_id}</DialogTitle></DialogHeader>
          {detailDialog.loading && <p className="text-sm text-slate-500 py-6 text-center">Loading…</p>}
          {detailDialog.data && (() => {
            const { mandate: md, application: app, instalments, summary: s } = detailDialog.data;
            const fi = s.first_instalment;
            return (
              <div className="space-y-4">
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-2 text-sm p-3 bg-slate-50 rounded-lg" data-testid="detail-identity">
                  <div><p className="text-[11px] text-slate-500">Loan Ref</p><p className="font-mono font-semibold">{app?.application_number || md.matched_reference || '—'}</p></div>
                  <div><p className="text-[11px] text-slate-500">Customer</p><p className="font-medium">{app?.full_name || md.matched_customer || (md.off_book ? `Off-book: ${md.manual_reference}` : '—')}</p></div>
                  <div><p className="text-[11px] text-slate-500">ID Number</p><p className="font-mono">{app?.id_number || md.debtor_id_number || '—'}</p></div>
                  <div><p className="text-[11px] text-slate-500">Mandate ID</p><p className="font-mono font-semibold">{md.mandate_id}</p></div>
                  <div><p className="text-[11px] text-slate-500">Contract Ref</p><p className="font-mono text-xs">{md.contract_reference || md.client_ref || '—'}</p></div>
                  <div><p className="text-[11px] text-slate-500">Bank / Account No</p><p>{(app?.bank_name || md.debtor_bank || '—')}{app?.account_number ? ` · ${app.account_number}` : ''}</p></div>
                  <div><p className="text-[11px] text-slate-500">Collection Day</p><p>{md.collection_day || '—'}{md.frequency ? ` · ${md.frequency}` : ''}</p></div>
                  <div><p className="text-[11px] text-slate-500">Tracking</p><p>{md.tracking_days ? `${md.tracking_days} day(s)` : '—'}{md.tracking_until ? ` · until ${md.tracking_until}` : ''}</p></div>
                  <div><p className="text-[11px] text-slate-500">Mandate State</p><p className="capitalize">{md.mandate_state || md.section}{md.mandate_reject_reason ? <span className="block text-[11px] text-red-600">{md.mandate_reject_reason}</span> : null}</p></div>
                  {app?.paid_out && <div><p className="text-[11px] text-slate-500">Paid Out</p><p className="text-teal-700 font-semibold">{app.payout_date}</p></div>}
                </div>

                <div className="grid grid-cols-3 sm:grid-cols-6 gap-2" data-testid="detail-financials">
                  <div className="rounded-lg border border-slate-200 p-2"><p className="text-[10px] text-slate-500">CAPITAL</p><p className="font-bold tabular-nums text-sm">{fmtR(s.capital)}</p></div>
                  <div className="rounded-lg border border-blue-100 bg-blue-50/50 p-2"><p className="text-[10px] text-blue-600">EXPECTED TOTAL</p><p className="font-bold tabular-nums text-sm text-blue-700">{fmtR(s.expected_total)}</p></div>
                  <div className="rounded-lg border border-emerald-100 bg-emerald-50/50 p-2"><p className="text-[10px] text-emerald-600">REVENUE (COLLECTED)</p><p className="font-bold tabular-nums text-sm text-emerald-700">{fmtR(s.collected)}</p></div>
                  <div className="rounded-lg border border-slate-200 p-2"><p className="text-[10px] text-slate-500">FEES (TPF)</p><p className="font-bold tabular-nums text-sm">{fmtR(md.tpf_value_report || md.pif_value)}</p></div>
                  <div className={`rounded-lg border p-2 ${s.profit >= 0 ? 'border-teal-100 bg-teal-50/50' : 'border-red-100 bg-red-50/50'}`}><p className="text-[10px] text-slate-500">PROFIT (SO FAR)</p><p className={`font-bold tabular-nums text-sm ${s.profit >= 0 ? 'text-teal-700' : 'text-red-700'}`}>{fmtR(s.profit)}</p></div>
                  <div className="rounded-lg border border-teal-100 p-2"><p className="text-[10px] text-slate-500">EXPECTED PROFIT</p><p className="font-bold tabular-nums text-sm text-teal-700">{fmtR(s.expected_profit)}</p></div>
                </div>

                <div className="flex flex-wrap items-center gap-2 text-sm" data-testid="detail-instalments-flag">
                  <Badge variant="outline" className={s.multiple_instalments ? 'bg-purple-100 text-purple-700 border-0' : 'bg-slate-100 text-slate-600 border-0'}>
                    {s.multiple_instalments ? `Multiple instalments — ${s.instalments_total}` : 'Single instalment'}
                  </Badge>
                  {md.instalment_amount ? <span className="text-xs text-slate-500">{fmtR(md.instalment_amount)} per instalment</span> : null}
                </div>

                {fi && (
                  <div className={`rounded-lg border p-3 text-sm ${fi.status === 'success' ? (fi.skipped_to_new_month ? 'border-amber-200 bg-amber-50/60' : 'border-emerald-200 bg-emerald-50/60') : (fi.status === 'failed' || fi.status === 'reversed') ? 'border-red-200 bg-red-50/60' : 'border-amber-200 bg-amber-50/60'}`} data-testid="first-instalment-outcome">
                    <p className="font-semibold text-xs text-slate-600 mb-0.5">1ST INSTALMENT (per transaction report)</p>
                    {fi.status === 'success' ? (
                      <p className={fi.skipped_to_new_month ? 'text-amber-800' : 'text-emerald-800'}>
                        ✓ Went off on {fi.actual_date}{fi.skipped_to_new_month ? ` — SKIPPED into a new month (was due ${fi.expected_date})` : ' — on schedule'}
                        {fi.original_instalment === 'No' ? ' · rescheduled instalment' : ''}
                      </p>
                    ) : fi.status === 'failed' || fi.status === 'reversed' ? (
                      <p className="text-red-800">✗ Did NOT go off — {fi.response_reason || 'failed'} on {fi.actual_date}{md.section === 'tracking' ? ' · now in tracking' : ''}</p>
                    ) : (
                      <p className="text-amber-800">{md.section === 'tracking' ? `In tracking${md.tracking_until ? ` until ${md.tracking_until}` : ''} — not yet collected` : `Scheduled for ${fi.expected_date || fi.actual_date || 'TBC'} — not yet collected`}</p>
                    )}
                  </div>
                )}

                {instalments.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-slate-600 mb-1">Instalment history</p>
                    <div className="border rounded overflow-x-auto">
                      <table className="w-full text-xs" data-testid="instalment-history-table">
                        <thead><tr className="text-left text-slate-500 border-b bg-slate-50"><th className="p-2">#</th><th className="p-2">Action Date</th><th className="p-2">Amount</th><th className="p-2">Status</th><th className="p-2">Reason / Response</th></tr></thead>
                        <tbody>
                          {instalments.map((r, i) => {
                            const c = SECTION_CFG[r.section] || { label: r.section, class: 'bg-slate-100 text-slate-600' };
                            return (
                              <tr key={i} className="border-b border-slate-50">
                                <td className="p-2">{r.instalment_number || 1}{r.instalments_total ? ` of ${r.instalments_total}` : ''}</td>
                                <td className="p-2 tabular-nums">{r.action_date || r.cycle_date || '—'}</td>
                                <td className="p-2 tabular-nums font-medium">{fmtR(r.total_amount)}</td>
                                <td className="p-2"><Badge variant="outline" className={`${c.class} border-0 text-[10px]`}>{c.label}</Badge></td>
                                <td className="p-2 text-slate-500">{r.response_reason || (r.section === 'success' && r.response_datetime ? `collected ${r.response_datetime.slice(0, 10)}` : '—')}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
                {md.manual_note && <p className="text-xs text-slate-500 italic flex items-center gap-1"><StickyNote className="w-3 h-3 text-amber-500" />{md.manual_note}{md.manual_note_by ? ` — ${md.manual_note_by}` : ''}</p>}
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>

      <Dialog open={editDialog.open} onOpenChange={(o) => setEditDialog(d => ({ ...d, open: o }))}>
        <DialogContent className="max-w-md" data-testid="edit-mandate-dialog" onPaste={handlePaste}>
          <DialogHeader><DialogTitle>Edit Mandate {editDialog.mandate?.mandate_id}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {editDialog.mandate?.matched_reference && (
              <p className="text-xs text-slate-500">Linked loan: <span className="font-mono font-semibold">{editDialog.mandate.matched_reference}</span> — {editDialog.mandate.matched_customer}. Changes pull through to the loan & customer portal.</p>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Instalment Date</Label>
                <Input type="date" value={editForm.action_date} onChange={e => setEditForm(f => ({ ...f, action_date: e.target.value }))} data-testid="edit-action-date" />
              </div>
              <div className="space-y-1.5">
                <Label>No. of Instalments</Label>
                <Input type="number" min="1" value={editForm.instalments_total} onChange={e => setEditForm(f => ({ ...f, instalments_total: e.target.value }))} placeholder="e.g. 1" data-testid="edit-instalments-total" />
              </div>
              <div className="space-y-1.5">
                <Label>Amount Due (R)</Label>
                <Input type="number" step="0.01" value={editForm.total_amount} onChange={e => setEditForm(f => ({ ...f, total_amount: e.target.value }))} data-testid="edit-total-amount" />
              </div>
              <div className="space-y-1.5">
                <Label>Status</Label>
                <Select value={editForm.section} onValueChange={v => setEditForm(f => ({ ...f, section: v }))}>
                  <SelectTrigger data-testid="edit-section-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="success">Success (Repaid)</SelectItem>
                    <SelectItem value="future">Future</SelectItem>
                    <SelectItem value="tracking">Tracking</SelectItem>
                    <SelectItem value="failed">Failed (Overdue)</SelectItem>
                    <SelectItem value="reversed">Reversed</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Manual Note</Label>
              <textarea
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm min-h-[70px] focus:outline-none focus:ring-2 focus:ring-ring"
                placeholder="e.g. Client promised to pay Friday / arrangement made / off-book detail…"
                value={editForm.manual_note}
                onChange={e => setEditForm(f => ({ ...f, manual_note: e.target.value }))}
                maxLength={500}
                data-testid="edit-manual-note"
              />
            </div>
            <div
              className="rounded-lg border-2 border-dashed border-slate-200 p-3 text-center cursor-pointer hover:border-blue-300 transition-colors"
              onClick={() => shotFileRef.current?.click()}
              data-testid="paste-screenshot-zone"
            >
              <input type="file" accept="image/*" ref={shotFileRef} onChange={pickScreenshotFile} className="hidden" />
              {pastedImage ? (
                <img src={pastedImage.url} alt="Pasted screenshot" className="max-h-40 mx-auto rounded border" data-testid="pasted-screenshot-preview" />
              ) : (
                <p className="text-xs text-slate-500 flex items-center justify-center gap-1.5 py-3">
                  <ClipboardPaste className="w-4 h-4" />Paste a screenshot here (Ctrl+V) or click to choose an image — it attaches to the customer's loan documents
                </p>
              )}
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => setEditDialog({ open: false, mandate: null })}>Cancel</Button>
              <Button size="sm" onClick={saveEdit} disabled={savingEdit} data-testid="edit-mandate-save-btn">{savingEdit ? 'Saving…' : 'Save Changes'}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={linkDialog.open} onOpenChange={(o) => setLinkDialog(d => ({ ...d, open: o }))}>
        <DialogContent className="max-w-sm" data-testid="link-mandate-dialog">
          <DialogHeader><DialogTitle>Link Mandate {linkDialog.mandate?.mandate_id} to a Loan</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-slate-500">Mandate ref: <span className="font-mono">{linkDialog.mandate?.mandate_reference}</span><br />Amount: {fmtR(linkDialog.mandate?.total_amount)} · {linkDialog.mandate?.action_date}</p>
            <div className="space-y-1.5">
              <Label>2k2day Loan Reference</Label>
              <Input value={linkRef} onChange={e => setLinkRef(e.target.value)} placeholder="e.g. 12345678" data-testid="link-reference-input" />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => setLinkDialog({ open: false, mandate: null })}>Cancel</Button>
              <Button size="sm" onClick={linkMandate} disabled={linking || !linkRef.trim()} data-testid="link-mandate-confirm-btn">{linking ? 'Linking…' : 'Link & Apply Status'}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
